import { CallTypes, ReturnTypes } from "@sprycore/spry-api-client"
import SessionClient from "./SessionClient"

type AddFlowArgs = Omit<CallTypes.AddFlowArgs, "campaignKey" | "ip">
type ParticipantDataArgs = Omit<CallTypes.AddParticipantDataArgs, "campaignKey" | "sessionKey">

export default class FlowBuilder {
    __sessionClient: SessionClient
    __metadata: any = {}
    __participant: ParticipantDataArgs = {}
    __committedFlow?: ReturnTypes.Flow
    __tag: string | null = null

    constructor(sessionClient: SessionClient) {
        this.__sessionClient = sessionClient
    }

    get sessionClient() { return this.__sessionClient }

    /**
     * Merges more metadata into the flow data
     */
    addMetadata(metadata: any) {
        if (typeof metadata !== "object") { throw new Error("parameter must be an object")}
        this.__metadata = Object.assign(this.__metadata, metadata || {})
        return this
    }

    /**
     * @deprecated
     * Merges more participant data into the flow data
     */
    addParticipantData(participant: ParticipantDataArgs) {
        if (typeof participant !== "object") { throw new Error("parameter must be an object")}
        this.__participant = Object.assign(this.__participant, participant || {})
        return this
    }

    get metadata() { return this.__metadata }
    /** @deprecated */
    get participant() { return this.__participant }

    /** @deprecated */
    get firstName() { return this.__participant.firstName }
    /** @deprecated */
    set firstName(firstName: string | null | undefined) { this.setFirstName(firstName) }
    /** @deprecated */
    setFirstName(firstName: string | null | undefined) { 
        if (firstName) { this.__participant.firstName = firstName }
        else { delete this.__participant.firstName }
        return this
    }
    
    /** @deprecated */
    get lastName() { return this.__participant.lastName }
    /** @deprecated */
    set lastName(lastName: string | null | undefined) { this.setLastName(lastName) }
    /** @deprecated */
    setLastName(lastName: string | null | undefined) { 
        if (lastName) { this.__participant.lastName = lastName }
        else { delete this.__participant.lastName }
        return this
    }
    
    /** @deprecated */
    get email() { return this.__participant.email }
    /** @deprecated */
    set email(email: string | null | undefined) { this.setEmail(email) }
    /** @deprecated */
    setEmail(email: string | null | undefined) { 
        if (email) { this.__participant.email = email }
        else { delete this.__participant.email }
        return this
    }
    
    /** @deprecated */
    get phone() { return this.__participant.phone }
    /** @deprecated */
    set phone(phone: string | null | undefined) { this.setPhone(phone) }
    /** @deprecated */
    setPhone(phone: string | null | undefined) { 
        if (phone) { this.__participant.phone = phone }
        else { delete this.__participant.phone }
        return this
    }
    
    /** @deprecated */
    get address() { return this.__participant.address }
    /** @deprecated */
    set address(address: string | null | undefined) { this.setAddress(address) }
    /** @deprecated */
    setAddress(address: string | null | undefined) { 
        if (address) { this.__participant.address = address }
        else { delete this.__participant.address }
        return this 
    }
    
    /** @deprecated */
    get address2() { return this.__participant.address2 }
    /** @deprecated */
    set address2(address2: string | null | undefined) { this.setAddress2(address2) }
    /** @deprecated */
    setAddress2(address2: string | null | undefined) { 
        if (address2) { this.__participant.address2 = address2 }
        else { delete this.__participant.address2 }
        return this
    }
    
    /** @deprecated */
    get city() { return this.__participant.city }
    /** @deprecated */
    set city(city: string | null | undefined) { this.setCity(city) }
    /** @deprecated */
    setCity(city: string | null | undefined) { 
        if (city) { this.__participant.city = city }
        else { delete this.__participant.city }
        return this
    }
    
    /** @deprecated */
    get province() { return this.__participant.province }
    /** @deprecated */
    set province(province: string | null | undefined) { this.setProvince(province) }
    /** @deprecated */
    setProvince(province: string | null | undefined) { 
        if (province) { this.__participant.province = province }
        else { delete this.__participant.province }
        return this
    }
    
    /** @deprecated */
    get postal() { return this.__participant.postal }
    /** @deprecated */
    set postal(postal: string | null | undefined) { this.setPostal(postal) }
    /** @deprecated */
    setPostal(postal: string | null | undefined) { 
        if (postal) { this.__participant.postal = postal }
        else { delete this.__participant.postal }
        return this
    }
    
    /** @deprecated */
    get country() { return this.__participant.country }
    /** @deprecated */
    set country(country: string | null | undefined) { this.setCountry(country) }
    /** @deprecated */
    setCountry(country: string | null | undefined) { 
        if (country) { this.__participant.country = country }
        else { delete this.__participant.country }
        return this
    }
    
    /** @deprecated */
    get preferredLanguage() { return this.__participant.country }
    /** @deprecated */
    set preferredLanguage(preferredLanguage: string | null | undefined) { this.setPreferredLanguage(preferredLanguage) }
    /** @deprecated */
    setPreferredLanguage(preferredLanguage: string | null | undefined) { 
        if (preferredLanguage) { this.__participant.preferredLanguage = preferredLanguage }
        else { delete this.__participant.preferredLanguage }
        return this
    }

    get tag() { return this.__tag }
    set tag(tag: string | null | undefined) { this.setTag(tag) }
    setTag(tag: string | null | undefined) {
        this.__tag = tag || null
        return this
    }

    /**
     * Returns a flow object if commit() has been successfully called
     */
    get committedFlow() { return this.__committedFlow || null }

    __createAddFlowArgs() {
        const args: AddFlowArgs = {
            sessionKey: this.__sessionClient.sessionKey
        }
        if (this.__tag) { args.tag = this.__tag }
        if (Object.keys(this.__metadata).length) { args.metadata = this.__metadata }
        if (Object.keys(this.__participant).length) { args.participant = this.__participant }
        return args
    }

    async commit() {
        if (this.__committedFlow) { 
            console.warn("The flow was already committed. If changes occurred since initial flow commitment, they have been ignored.")
            return this.__committedFlow
        }
        const body = this.__createAddFlowArgs()
        this.__committedFlow = await this.__sessionClient.call<ReturnTypes.Flow>("addFlow", body)
        return this.__committedFlow
    }

    /** @deprecated */
    async call<T>(method: string): Promise<T> {
        const args = this.__createAddFlowArgs()
        return await this.__sessionClient.call<T>(method, args)
    }
}